import type { FilterOption } from 'bundles/enterprise-ui/components/PopoverFilter';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

/**
 * Sentinel `sentBy` value meaning "no author filter". Any non-empty userId
 * string from the admin list is a valid specific selection.
 */
export const SENT_BY_ALL = 'all';

/**
 * The two screens that can open the Preview screen inside the message modal.
 * Preview's Back button returns to whichever one launched it.
 */
export const PREVIEW_RETURN_SCREEN = {
  REVIEW: 'review',
  MANUAL: 'manual',
} as const;

export type PreviewReturnScreen = (typeof PREVIEW_RETURN_SCREEN)[keyof typeof PREVIEW_RETURN_SCREEN];

/**
 * Active filter selections for the Messaging landing page's Sent Messages
 * table. Each key corresponds to one of the three filter triggers rendered
 * by the `FilterBar` and matches an option `value` returned by the option
 * factories in this module.
 */
export type MessagingFilters = {
  /**
   * Selected sender. `'all'` means no filter; any other value is the admin's
   * userId (as a string) to pass as the `authorUserId` query param.
   */
  sentBy: string;
  /** How often the message is sent — one-time, auto-send, or paused. */
  frequency: 'all' | 'ONE_TIME' | 'AUTO_SEND' | 'PAUSED';
  /** Time window over which messages are shown. */
  timePeriod: '30_days' | '90_days' | '1_year';
};

/**
 * Initial filter state for the Messaging landing page. Also used as the
 * fallback when an individual filter is cleared by the user.
 */
export const DEFAULT_MESSAGING_FILTERS: MessagingFilters = {
  sentBy: SENT_BY_ALL,
  frequency: 'all',
  timePeriod: '30_days',
};

/**
 * Builds the **Sent by** option list from the supplied admin entries.
 * Exposed as a function because the labels use `_t()`, which must be
 * invoked at render time so locale changes propagate.
 */
export const getSentByOptions = (admins: { userId: number; fullName: string }[]): FilterOption[] => [
  { value: SENT_BY_ALL, label: _t('All') },
  ...admins.map((admin) => ({ value: String(admin.userId), label: admin.fullName })),
];

/**
 * Options for the **Frequency** filter. Values match the backend enum so they
 * can be passed directly as the `frequency` query param.
 *
 * @see Factory rationale on {@link getSentByOptions}.
 */
export const getFrequencyOptions = (): FilterOption[] => [
  { value: 'all', label: _t('All') },
  { value: 'ONE_TIME', label: _t('One time') },
  { value: 'AUTO_SEND', label: _t('Auto-send') },
  { value: 'PAUSED', label: _t('Paused') },
];

/**
 * Options for the **Time period** filter. Listed in ascending chronological
 * order so the dropdown reads naturally top-to-bottom.
 *
 * @see Factory rationale on {@link getSentByOptions}.
 */
export const getTimePeriodOptions = (): FilterOption[] => [
  { value: '30_days', label: _t('30 days') },
  { value: '90_days', label: _t('90 days') },
  { value: '1_year', label: _t('1 year') },
];

/**
 * Number of days each `timePeriod` value represents. Used by
 * `filterSentMessages` to compute the cutoff window.
 */
export const TIME_PERIOD_DAYS: Record<MessagingFilters['timePeriod'], number> = {
  '30_days': 30,
  '90_days': 90,
  '1_year': 365,
};

/** CTA enum values returned by the AI email generation API. */
export type CtaEnumValue = 'JOIN_PROGRAM' | 'FIND_A_COURSE' | 'DO_NOT_INCLUDE' | 'CONTINUE_LEARNING';

/**
 * Maps the CTA enum returned by the generate API to its user-facing display
 * label for the Review and send screen. `DO_NOT_INCLUDE` maps to an empty
 * string (no CTA button in the email).
 */
export const getCtaDisplayLabel = (ctaEnum: string): string => {
  switch (ctaEnum) {
    case 'JOIN_PROGRAM':
      return _t('Join program');
    case 'FIND_A_COURSE':
      return _t('Find a course');
    case 'CONTINUE_LEARNING':
      return _t('Continue learning');
    case 'DO_NOT_INCLUDE':
      return '';
    default:
      return ctaEnum;
  }
};
